import { getFullUrl } from 'hubspot-url-utils';
import PortalIdParser from 'PortalIdParser';
import { AppCitationTypename } from '../../../__generated__/com/hubspot/breeze/engine/shared/models/citation/Citation';
export { AppCitationTypename };

// Canonical [cite-xxx] inline-marker shape, shared by every citation resolver so
// the id format lives in one place. Returned as a fresh instance per call: a
// shared global-flagged RegExp carries `lastIndex` state across .test()/.replace()
// and would leak between callers.
export function createCiteMarkerRegex() {
  return /\[cite-[a-z0-9]+\]/g;
}
export function convertAppCitationEntry(entry) {
  if (entry.__typename !== AppCitationTypename || !entry.citationId) return null;
  const value = entry.value;
  const rawType = value.citationType;
  const citationType = typeof rawType === 'string' ? rawType : undefined;
  if (citationType !== 'MEMORY' && citationType !== 'CHAT_MESSAGE') return null;
  const title = typeof value.title === 'string' ? value.title : '';
  const inAppIds = typeof value.inAppIds === 'object' && value.inAppIds !== null ? value.inAppIds : undefined;
  return Object.assign({
    id: entry.citationId,
    title,
    citationType
  }, inAppIds !== undefined ? {
    inAppIds
  } : {});
}
export function convertToSearchResultMarkers(text, citations) {
  const idToIndex = new Map();
  const seenChipIds = new Set();
  const resolved = [];
  let nextIndex = 1;
  const converted = text.replace(createCiteMarkerRegex(), match => {
    const citationId = match.slice(1, -1);
    const citation = citations.find(c => c.id === citationId);
    if (!citation) return match;
    if (citation.displayType === 'RECORD_CHIP') {
      var _citation$title;
      if (!seenChipIds.has(citationId)) {
        seenChipIds.add(citationId);
        resolved.push(citation);
      }
      return `[CRM_RECORD:${citation.objectTypeId}:${citation.objectId}:${encodeURIComponent((_citation$title = citation.title) !== null && _citation$title !== void 0 ? _citation$title : '')}]`;
    }
    let index = idToIndex.get(citationId);
    if (index === undefined) {
      index = nextIndex++;
      idToIndex.set(citationId, index);
      resolved.push(Object.assign({}, citation, {
        index
      }));
    }
    return `[SEARCH_RESULT_${index}]`;
  });
  return {
    text: converted,
    citations: resolved
  };
}
export function extractDomain(url) {
  try {
    return new URL(url).hostname;
  } catch (_unused) {
    return url;
  }
}
export function parseRichContentCitations(content) {
  const result = new Map();
  try {
    const parsed = JSON.parse(content);
    if (parsed === null || typeof parsed !== 'object') return result;
    const raw = parsed.citations;
    if (!Array.isArray(raw)) return result;
    for (const item of raw) {
      if (item === null || typeof item !== 'object' || item._type !== 'RichContentCitation' || typeof item.id !== 'string' || typeof item.title !== 'string') {
        continue;
      }
      const {
        id,
        title,
        additionalContent
      } = item;
      result.set(id, {
        title,
        additionalContent
      });
    }
  } catch (_unused2) {
    // non-JSON content
  }
  return result;
}
function isMobileSurface() {
  if (typeof navigator === 'undefined') return false;
  return /hubspot-mobile/i.test(navigator.userAgent);
}
function resolveSurfaceUrl(obj) {
  if (!Array.isArray(obj.urls) || obj.urls.length === 0) return null;
  const surface = isMobileSurface() ? 'MOBILE' : 'EMBEDDED';
  for (const entry of obj.urls) {
    if (entry !== null && typeof entry === 'object' && entry.clientSurface === surface && typeof entry.url === 'string') {
      return entry.url;
    }
  }
  return null;
}
function resolveUrl(obj, resolveCitationUrl) {
  if (typeof obj.url === 'string') return obj.url;

  // Check urls array for surface-specific URL
  const surfaceUrl = resolveSurfaceUrl(obj);
  if (surfaceUrl !== null) return surfaceUrl;
  if (resolveCitationUrl !== undefined) {
    let resolved;
    try {
      resolved = resolveCitationUrl(obj);
    } catch (_unused3) {
      return null;
    }
    if (typeof resolved === 'string') return resolved;
  }
  if (obj._type === 'CrmObjectCitation') return buildCrmGoToUrl(obj);
  return null;
}
function buildCrmGoToUrl(obj) {
  if (typeof obj.objectTypeId !== 'string' || typeof obj.objectId !== 'string' && typeof obj.objectId !== 'number') {
    return null;
  }
  const portalId = PortalIdParser.get();
  if (portalId == null) return null;
  return `${getFullUrl('app')}/go-to/${portalId}/${obj.objectTypeId}/${obj.objectId}`;
}
function parseCrmProperty(raw) {
  if (raw === null || typeof raw !== 'object') return undefined;
  const obj = raw;
  const name = typeof obj.name === 'string' ? obj.name : '';
  if (!name) return undefined;
  return {
    name,
    label: typeof obj.label === 'string' ? obj.label : '',
    value: typeof obj.value === 'string' ? obj.value : ''
  };
}
function parseCrmProperties(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseCrmProperty).filter(property => property !== undefined);
}
export function parseCitationsFromToolResult(content, resolveCitationUrl) {
  try {
    const parsed = JSON.parse(content);
    if (parsed === null || typeof parsed !== 'object' || !Array.isArray(parsed.citations)) {
      return [];
    }
    const raw = parsed.citations;
    const result = [];
    for (const item of raw) {
      var _obj$_type;
      if (item === null || typeof item !== 'object') continue;
      const obj = item;
      if (typeof obj.id !== 'string') continue;
      const rawType = (_obj$_type = obj._type) !== null && _obj$_type !== void 0 ? _obj$_type : obj.__typename;
      const explicitCitationType = typeof obj.citationType === 'string' ? obj.citationType : undefined;
      const citationType = explicitCitationType !== null && explicitCitationType !== void 0 ? explicitCitationType : typeof rawType === 'string' ? rawType : undefined;
      if (citationType === 'CrmObjectCitation' && obj.displayType === 'RECORD_CHIP' && typeof obj.objectTypeId === 'string' && obj.objectId != null) {
        var _resolveUrl;
        const chipUrl = (_resolveUrl = resolveUrl(obj, resolveCitationUrl)) !== null && _resolveUrl !== void 0 ? _resolveUrl : '';
        const chip = {
          id: obj.id,
          url: chipUrl,
          title: typeof obj.title === 'string' ? obj.title : obj.objectId != null ? String(obj.objectId) : '',
          displayType: 'RECORD_CHIP',
          objectTypeId: obj.objectTypeId,
          objectId: String(obj.objectId),
          primaryProperty: parseCrmProperty(obj.primaryProperty),
          secondaryProperties: parseCrmProperties(obj.secondaryProperties)
        };
        result.push(chip);
        continue;
      }
      const inAppIds = typeof obj.inAppIds === 'object' && obj.inAppIds !== null ? obj.inAppIds : undefined;
      const url = resolveUrl(obj, resolveCitationUrl);
      if (url === null && inAppIds === undefined) continue;
      const title = typeof obj.title === 'string' ? obj.title : url !== null && url !== void 0 ? url : obj.id;
      const web = Object.assign({
        id: obj.id,
        title
      }, url !== null ? {
        url
      } : {}, citationType !== undefined ? {
        citationType
      } : {}, inAppIds !== undefined ? {
        inAppIds
      } : {});
      result.push(web);
    }
    return result;
  } catch (_unused4) {
    return [];
  }
}